import React, { useMemo, useState } from "react";
import { useCredentialsContext } from "../context/CredentialsContext";
import { extendCredentialExpiration } from "../services/api";
import {
    expirationLabels,
    formatExpirationDate,
    getDaysUntilExpiration,
    getExpirationStatus,
} from "../utils/expiration";

export default function ExpirationManagement() {
    const { credentials, loading, error, refresh } = useCredentialsContext();
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("expires_at");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkDuration, setBulkDuration] = useState("30-days");
    const [bulkError, setBulkError] = useState(null);
    const [bulkUpdating, setBulkUpdating] = useState(false);

    const summary = useMemo(() => {
        const tally = {
            expired: 0,
            "expiring-soon": 0,
            active: 0,
            "no-expiration": 0,
        };
        credentials.forEach((credential) => {
            const status = getExpirationStatus(credential.expires_at);
            if (tally[status] !== undefined) {
                tally[status] += 1;
            }
        });
        return tally;
    }, [credentials]);

    const filteredCredentials = useMemo(() => {
        const term = searchTerm.toLowerCase();
        const list = credentials.filter((credential) => {
            const company = (credential.company || "").toLowerCase();
            const provider = (credential.provider || "").toLowerCase();
            const name = `${credential.first_name || ""} ${credential.last_name || ""}`
                .trim()
                .toLowerCase();
            const matchesTerm =
                company.includes(term) ||
                provider.includes(term) ||
                name.includes(term);
            if (!matchesTerm) return false;
            if (selectedStatus === "all") return true;
            return getExpirationStatus(credential.expires_at) === selectedStatus;
        });

        return list.sort((a, b) => {
            if (sortBy === "company") {
                return (a.company || "").localeCompare(b.company || "");
            }
            if (sortBy === "status") {
                return getExpirationStatus(a.expires_at).localeCompare(
                    getExpirationStatus(b.expires_at)
                );
            }
            const aDate = a.expires_at ? new Date(a.expires_at).getTime() : Infinity;
            const bDate = b.expires_at ? new Date(b.expires_at).getTime() : Infinity;
            return aDate - bDate;
        });
    }, [credentials, searchTerm, selectedStatus, sortBy]);

    const toggleSelection = (credentialId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(credentialId)) {
                next.delete(credentialId);
            } else {
                next.add(credentialId);
            }
            return next;
        });
    };

    const handleBulkExtend = async () => {
        if (selectedIds.size === 0 || bulkUpdating) return;
        setBulkError(null);
        setBulkUpdating(true);
        try {
            for (const credentialId of selectedIds) {
                await extendCredentialExpiration(credentialId, bulkDuration);
            }
            setSelectedIds(new Set());
            await refresh();
        } catch (err) {
            console.error("Failed to extend expirations", err);
            setBulkError(err.message || "Unable to extend expirations.");
        } finally {
            setBulkUpdating(false);
        }
    };

    const handleSendReminders = () => {
        setBulkError(
            "Reminder emails are sent automatically by the scheduled notification job."
        );
    };

    return (
        <main className="expiration-management container">
            <div className="expiration-management-header">
                <h1>Expiration Management</h1>
                <p className="text-muted">
                    Review expiring credentials and take bulk actions.
                </p>
            </div>

            <div className="expiration-summary-grid">
                <div className="summary-item">
                    <span className="summary-label">Active</span>
                    <span className="summary-value">{summary.active}</span>
                </div>
                <div className="summary-item warning">
                    <span className="summary-label">Expiring soon</span>
                    <span className="summary-value">{summary["expiring-soon"]}</span>
                </div>
                <div className="summary-item danger">
                    <span className="summary-label">Expired</span>
                    <span className="summary-value">{summary.expired}</span>
                </div>
                <div className="summary-item muted">
                    <span className="summary-label">No expiration</span>
                    <span className="summary-value">{summary["no-expiration"]}</span>
                </div>
            </div>

            <div className="expiration-controls">
                <input
                    type="search"
                    className="search-input"
                    placeholder="Search by company, provider, or client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                >
                    <option value="all">All statuses</option>
                    <option value="active">{expirationLabels.active}</option>
                    <option value="expiring-soon">
                        {expirationLabels["expiring-soon"]}
                    </option>
                    <option value="expired">{expirationLabels.expired}</option>
                    <option value="no-expiration">
                        {expirationLabels["no-expiration"]}
                    </option>
                </select>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="expires_at">Sort by expiration</option>
                    <option value="company">Sort by company</option>
                    <option value="status">Sort by status</option>
                </select>
            </div>

            <div className="expiration-bulk-actions">
                <select
                    value={bulkDuration}
                    onChange={(e) => setBulkDuration(e.target.value)}
                >
                    <option value="1-week">Extend 1 week</option>
                    <option value="30-days">Extend 30 days</option>
                    <option value="until-complete">Until complete</option>
                </select>
                <button
                    className="btn"
                    disabled={bulkUpdating || selectedIds.size === 0}
                    onClick={handleBulkExtend}
                >
                    {bulkUpdating ? "Updating..." : "Extend selected"}
                </button>
                <button className="btn cancel-btn" onClick={handleSendReminders}>
                    Send reminders
                </button>
                {bulkError && <p className="error-message">{bulkError}</p>}
            </div>

            {loading && <p>Loading credentials...</p>}
            {error && (
                <p className="error-message">
                    Unable to load expiration data. Please try again later.
                </p>
            )}
            {!loading && !error && filteredCredentials.length === 0 && (
                <p>No credentials match your filters.</p>
            )}
            {!loading && !error && filteredCredentials.length > 0 && (
                <div className="expiration-table">
                    <div className="expiration-row header">
                        <span />
                        <span>Company</span>
                        <span>Provider</span>
                        <span>Client</span>
                        <span>Status</span>
                        <span>Expires</span>
                    </div>
                    {filteredCredentials.map((credential) => {
                        const status = getExpirationStatus(credential.expires_at);
                        const daysRemaining = getDaysUntilExpiration(
                            credential.expires_at
                        );
                        const daysLabel =
                            daysRemaining !== null ? Math.abs(daysRemaining) : null;
                        const clientName = [
                            credential.first_name,
                            credential.last_name,
                        ]
                            .filter(Boolean)
                            .join(" ");
                        return (
                            <div className="expiration-row" key={credential.id}>
                                <input
                                    type="checkbox"
                                    checked={selectedIds.has(credential.id)}
                                    onChange={() => toggleSelection(credential.id)}
                                />
                                <span>{credential.company}</span>
                                <span>{credential.provider}</span>
                                <span>{clientName || "N/A"}</span>
                                <span
                                    className={`expiration-badge expiration-status ${status}`}
                                    aria-label={`Expiration status: ${expirationLabels[status]}`}
                                >
                                    {expirationLabels[status]}
                                </span>
                                <span>
                                    {credential.expires_at
                                        ? formatExpirationDate(
                                              credential.expires_at
                                          )
                                        : "No expiration"}
                                    {daysLabel !== null &&
                                        status !== "no-expiration" && (
                                            <span className="expiration-days">
                                                {" "}
                                                ({daysLabel}d)
                                            </span>
                                        )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </main>
    );
}
